import React, {
  CSSProperties,
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Link from 'next/link';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import HighlightedText from '../highlighted-text/highlighted-text';
import {
  GetSearchSuggestions,
  SearchApiSettings,
  getSearchSuggestionsFromApi,
} from '../../../api-clients/search-api-client';
import styles from './typeahead.module.scss';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useAsyncData } from '../../../hooks/use-async-data';
import { debounce } from '../../../utils/function-helpers/debounce';

export type Props = {
  typeaheadInputId: string;
  typeaheadInputPlaceholder: string;
  typeaheadInputInitialValue: string;
  hideTypeaheadTrigger: boolean;
  typeaheadInputValueChange?: (keyword: string) => void;
  typeaheadAutoSuggestSelected?: (keyword: string) => void;
  cssWrapper: string;
  minimumTextLength: number;
  searchApiSettings: SearchApiSettings;
  router?: AppRouterInstance;
  currentLocale: string;
  themeCopyColor?: string;
  getSearchSuggestions?: GetSearchSuggestions;
  resetTypeahead?: boolean;
  invertedTheme?: boolean;
};

const INPUT_DEBOUNCE_MS = 500;

export default function Typeahead({
  typeaheadInputId,
  typeaheadInputPlaceholder,
  typeaheadInputInitialValue,
  hideTypeaheadTrigger,
  typeaheadInputValueChange,
  typeaheadAutoSuggestSelected,
  cssWrapper,
  minimumTextLength = 10,
  searchApiSettings,
  router,
  currentLocale,
  themeCopyColor,
  resetTypeahead,
  invertedTheme,
  getSearchSuggestions = getSearchSuggestionsFromApi,
}: Props) {
  const [searchInputValue, setSearchInputValue] = useState<string>(
    typeaheadInputInitialValue || ``,
  );

  const inputRef = useRef<HTMLInputElement>();

  // Typeahead
  const [showTypeaheadList, setShowTypeaheadList] = useState<boolean>(false);
  const [selectedAutoSuggestResult, setSelectedAutoSuggestResult] = useState<string>(``);

  // for accessibility
  const [focusedResultIndex, setFocusedResultIndex] = useState<number>(0);

  /**
   * Checks if search text over min text length.
   */
  const hasValidValue = useCallback(
    () => inputRef?.current?.value?.trim().length >= minimumTextLength,
    [minimumTextLength],
  );

  // to capture input change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback(
    debounce((event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value || ``;

      setSearchInputValue(value);

      setShowTypeaheadList(hasValidValue());

      if (typeaheadInputValueChange) {
        typeaheadInputValueChange(value);
      }
    }, INPUT_DEBOUNCE_MS),
    [typeaheadInputValueChange],
  );

  const { data, isLoading } = useAsyncData(
    async (signal: AbortSignal) => {
      if (showTypeaheadList && hasValidValue()) {
        return getSearchSuggestions(searchInputValue, signal, searchApiSettings);
      }

      return Promise.resolve(null);
    },
    null,
    [searchInputValue, showTypeaheadList],
  );

  useEffect(() => {
    setSelectedAutoSuggestResult(``);
    setFocusedResultIndex(0);
  }, [data]);

  /**
   * Opens the Typeahead List Box.
   */
  function openListBox(): void {
    if (selectedAutoSuggestResult) {
      setFocusedResultIndex(
        data.findIndex((result) => result.displayText === selectedAutoSuggestResult),
      );
    }
    setShowTypeaheadList(true);
  }

  /**
   * Closes the Typeahead List Box.
   */
  const closeListBox = useCallback(() => {
    setShowTypeaheadList(false);
  }, []);

  // to hide the auto suggest list on out side click
  useEffect(() => {
    closeListBox();
  }, [closeListBox, hideTypeaheadTrigger]);

  /**
   * When we focus on the textbox
   */
  function handleFocusEvent(): void {
    if (hasValidValue()) {
      openListBox();
    }
  }

  function handleBlurEvent(): void {
    closeListBox();
  }

  /**
   * Initiates populations of selected/focused Typeahead List Item into search bar.
   */
  function selectFocusedResult(): void {
    if (!data || data?.length === 0 || !showTypeaheadList || !hasValidValue()) {
      return;
    }

    const result = data[focusedResultIndex];

    if (router) {
      const [pathname, query] = result.searchUrl.split(`?`);

      window.history.pushState(null, null, `${pathname}?${query}`);
    }

    if (typeaheadAutoSuggestSelected) {
      typeaheadAutoSuggestSelected(searchInputValue);
      closeListBox();
    }
  }

  /**
   * Submits the selected/focused result from the Typeahead List.
   * @param event The event
   */
  function handleEnterKey(event: KeyboardEvent): void {
    if (showTypeaheadList && !isLoading && data !== null) {
      event.preventDefault();
      selectFocusedResult();
    }
    if (!hasValidValue()) event.preventDefault();
  }

  /**
   * Focuses on the next item in the list.
   * If we're at the last item, then we start back at the beginning.
   * @param event The event
   */
  function focusNextResult(event: KeyboardEvent): void {
    event.preventDefault();

    if (data?.length === 0) {
      return;
    }

    const maxIndex = data.length - 1;

    if (focusedResultIndex === maxIndex) {
      setFocusedResultIndex(0);
    } else {
      setFocusedResultIndex(focusedResultIndex + 1);
    }
  }

  /**
   * Focuses on the previous item in the list.
   * If we're at the first item, then we start back at the end.
   * @param event The event
   */
  function focusPreviousResult(event: KeyboardEvent): void {
    event.preventDefault();

    if (data?.length === 0) {
      return;
    }

    const maxIndex = data.length - 1;

    if (focusedResultIndex === 0) {
      setFocusedResultIndex(maxIndex);
    } else {
      setFocusedResultIndex(focusedResultIndex - 1);
    }
  }

  /**
   * Updates focused result index
   * @param resultIndex Index of the typeahead search list item
   */
  function focusResult(resultIndex: number): void {
    setFocusedResultIndex(resultIndex);
  }

  /**
   * Captures keydown event on the search textbox
   * KeyboardEvent.key | https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
   * @param event The Keyboard event
   */
  function handleKeyDownEvent(event: KeyboardEvent): void {
    switch (event.key) {
      case `ArrowUp`:
        focusPreviousResult(event);
        break;
      case `ArrowDown`:
        focusNextResult(event);
        break;
      case `Tab`:
        selectFocusedResult();
        break;
      case `Enter`:
        handleEnterKey(event);
        break;
      case `Escape`:
        closeListBox();
        break;
      default:
        break;
    }
  }

  // Reset Typeahead on Cross Button click
  const clearSearchInput = useCallback(() => {
    setSearchInputValue('');
    setShowTypeaheadList(false);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (typeaheadInputValueChange) {
      typeaheadInputValueChange('');
    }
  }, [typeaheadInputValueChange]);

  useEffect(() => {
    if (resetTypeahead) {
      clearSearchInput();
    }
  }, [clearSearchInput, resetTypeahead]);

  const renderData = useMemo(() => {
    const inputStyle: CSSProperties = {
      '--typeahead-copy-color': themeCopyColor,
    } as CSSProperties;

    const inputClasses = [styles[`typeahead-control`]];

    if (showTypeaheadList && hasValidValue() && data?.length > 0) {
      inputClasses.push(styles[`typeahead-control-mobile`]);
    }

    if (searchInputValue) {
      inputClasses.push(styles[`typeahead-control-with-value`]);
    }

    const inputClassName = inputClasses.join(' ');

    return { inputStyle, inputClassName };
  }, [data?.length, hasValidValue, searchInputValue, showTypeaheadList, themeCopyColor]);
  
  return (
    <div className={`${styles[cssWrapper]} ${invertedTheme ? styles['inverted-theme'] : ''}`}>
      <label
        id={`${typeaheadInputId}-label`}
        htmlFor={`${typeaheadInputId}-input`}
        className={`${styles[`typeahead-label`]}`}
      >
        Search
      </label>
      <input
        style={renderData.inputStyle}
        autoComplete='off'
        type='search'
        name='search'
        ref={inputRef}
        defaultValue={typeaheadInputInitialValue}
        onFocus={handleFocusEvent}
        onBlur={handleBlurEvent}
        onChange={handleChange}
        onKeyDown={handleKeyDownEvent}
        onMouseDown={openListBox}
        className={renderData.inputClassName}
        id={`${typeaheadInputId}-input`}
        placeholder={typeaheadInputPlaceholder}
        role='combobox'
        aria-controls={`${typeaheadInputId}__listbox`}
        aria-expanded={showTypeaheadList && data?.length > 0 ? `true` : `false`}
        aria-activedescendant={
          showTypeaheadList ? `${typeaheadInputId}__option-${focusedResultIndex}` : ``
        }
        aria-owns={`${typeaheadInputId}__listbox`}
        aria-autocomplete='list'
      />
      {showTypeaheadList && (
        <ul
          id={`${typeaheadInputId}__listbox`}
          className={
            hasValidValue()
              ? `${styles[`typeahead-list`]} ${styles[`typeahead-list-styled`]} p-0 list-unstyled`
              : `${styles[`typeahead-list`]} p-0 list-unstyled`
          }
          role='listbox'
        >
          {isLoading && hasValidValue() && (
            <li className={`${styles[`typeahead-list-loader`]} p-2 text-center`}>
              <LoadingSpinner width={40} height={40} src={'/public/next-assets/loaders/into.svg'} />
            </li>
          )}
          {!isLoading && hasValidValue() && data?.length && (
            <>
              {data.map((result, index) => (
                <li
                  // eslint-disable-next-line react/no-array-index-key
                  key={`typeahead-search-option-${index}`}
                  id={`${typeaheadInputId}__option-${index}`}
                  className={
                    index === focusedResultIndex
                      ? `${styles[`typeahead-list_item`]} ${styles[`typeahead-list_item-active`]}`
                      : `${styles[`typeahead-list_item`]}`
                  }
                  onMouseOver={() => focusResult(index)}
                  onFocus={() => focusResult(index)}
                  onKeyDown={(event) => {
                    event.preventDefault();
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  role='option'
                  aria-selected={index === focusedResultIndex}
                >
                  <Link
                    href={`/${currentLocale}${result.searchUrl}`}
                    aria-label={`click here to get search results for ${result.displayText}`}
                    className='d-block text-decoration-none'
                    onClick={closeListBox}
                  >
                    <HighlightedText
                      searchValue={searchInputValue}
                      resultText={result.displayText}
                    />
                  </Link>
                </li>
              ))}
            </>
          )}
        </ul>
      )}
    </div>
  );
}
